"use client";

import React, { ReactNode, useEffect, useState, useCallback } from "react";
import styles from "./ModalManager.module.scss";
import Image from "next/image";
import assetLoader from "@utils/internalImageLoder";
import Text from "@components/atoms/Text";

const ANIMATION_DURATION = 300;

type ModalManagerProps = {
  isOpen: boolean;
  onClose?: () => void;
  children: ReactNode;
  title?: string | ReactNode;
  showClose?: boolean;
  customClass?: string;
  overlayClass?: string;
  modalBodyClass?: string;
  preventScroll?: boolean;
  modalHeaderClass?: string;
};

export const ModalManager = ({
  isOpen,
  onClose,
  children,
  title,
  showClose = true,
  customClass = "",
  overlayClass = "",
  preventScroll = true,
  modalBodyClass = "",
  modalHeaderClass = "",
}: ModalManagerProps) => {
  const [mounted, setMounted] = useState(false);
  const [active, setActive] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
    } else if (mounted) {
      setActive(false);
      const timer = setTimeout(() => setMounted(false), ANIMATION_DURATION);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  useEffect(() => {
    if (mounted && isOpen) {
      const raf = requestAnimationFrame(() => setActive(true));
      return () => cancelAnimationFrame(raf);
    }
  }, [mounted, isOpen]);

  useEffect(() => {
    if (mounted && preventScroll) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [mounted, preventScroll]);

  const handleClose = useCallback(() => {
    setActive(false);
    setTimeout(() => {
      onClose?.();
    }, ANIMATION_DURATION);
  }, [onClose]);

  if (!mounted) return null;

  return (
    <div
      className={`${styles.modalOverlay} ${active ? styles.overlayActive : ""} ${overlayClass}`}
      onClick={handleClose}
    >
      <div
        className={`${styles.modalContent} ${active ? styles.contentActive : ""} ${customClass}`}
        onClick={(e) => e.stopPropagation()}
      >
        {(title || showClose) && (
          <div className={`${styles.modalHeader} ${modalHeaderClass}`}>
            {title && <Text className={styles.modalTitle}>{title}</Text>}
            {showClose && (
              <Image
                src={`${assetLoader()}/icons/Close_Dark.svg`}
                alt="Close"
                width={24}
                loading="lazy"
                height={24}
                className={styles.modalClose}
                onClick={handleClose}
              />
            )}
          </div>
        )}
        <div className={`${styles.modalBody} ${modalBodyClass}`}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default ModalManager;
